import { useState, useEffect } from 'react';
import { MessageSquare, Send, Search, User, Clock, Users } from 'lucide-react';
import { useI18n } from '../i18n';

import { API_BASE } from '../config/api';

interface StaffMember {
    id: string;
    firstName: string;
    lastName: string;
    role: string;
    specialty: string | null;
}

interface Message {
    id: string;
    from: string;
    text: string;
    timestamp: Date;
    isOwn: boolean;
}

export function MessagesPage() {
    const { t } = useI18n();
    const [staff, setStaff] = useState<StaffMember[]>([]);
    const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        loadStaff();
    }, []);

    async function loadStaff() {
        try {
            const res = await fetch(`${API_BASE}/staff`);
            const json = await res.json();
            setStaff(json.data || []);
        } catch (err) {
            console.error('Failed to load staff:', err);
        }
    }

    function selectStaff(member: StaffMember) {
        setSelectedStaff(member);
        // In a real implementation, load FHIR Communication resources between current user and selected staff
        setMessages([
            {
                id: '1',
                from: member.firstName,
                text: `Hola, ¿necesitas algo sobre el paciente García?`,
                timestamp: new Date(Date.now() - 3600000),
                isOwn: false,
            },
        ]);
    }

    async function sendMessage() {
        if (!newMessage.trim() || !selectedStaff) return;
        const msg = {
            id: String(Date.now()),
            from: 'Yo',
            text: newMessage,
            timestamp: new Date(),
            isOwn: true,
        };
        setMessages(prev => [...prev, msg]);
        setNewMessage('');
        try {
            await fetch(`${API_BASE}/whatsapp/messages`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    recipientId: selectedStaff.id,
                    text: newMessage,
                    channel: 'internal',
                }),
            });
        } catch (err) {
            console.warn('Message delivery pending (API may be unavailable):', err);
        }
    }

    const filteredStaff = staff.filter(s => {
        if (!searchQuery) return true;
        return `${s.firstName} ${s.lastName}`.toLowerCase().includes(searchQuery.toLowerCase());
    });

    const roleLabels: Record<string, string> = {
        doctor: 'Médico',
        coordinator: 'Coordinador',
        receptionist: 'Recepcionista',
        nurse: 'Enfermera',
        admin: 'Administrador',
    };

    return (
        <div className="page-content fade-in" style={{ height: 'calc(100dvh - var(--header-height, 0px))', display: 'flex', flexDirection: 'column' }}>
            <div className="page-header" style={{ position: 'static', borderBottom: 'none', background: 'transparent', paddingLeft: 0, flexShrink: 0 }}>
                <div>
                    <h1 className="page-title">{t('nav.messages')}</h1>
                    <p className="page-subtitle">Mensajería interna del equipo clínico</p>
                </div>
            </div>

            <div style={{
                flex: 1, display: 'grid', gridTemplateColumns: '280px 1fr', gap: 0,
                border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden',
                background: 'var(--color-surface)',
            }}>
                {/* Staff List */}
                <div style={{ borderRight: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: 'var(--space-3)', borderBottom: '1px solid var(--color-border)' }}>
                        <div style={{ position: 'relative' }}>
                            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
                            <input
                                className="form-input"
                                style={{ paddingLeft: 32, fontSize: 'var(--text-sm)' }}
                                placeholder="Buscar personal..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>
                    <div style={{ flex: 1, overflow: 'auto' }}>
                        {filteredStaff.map(member => (
                            <div
                                key={member.id}
                                onClick={() => selectStaff(member)}
                                style={{
                                    padding: 'var(--space-3) var(--space-4)',
                                    display: 'flex', gap: 'var(--space-3)', alignItems: 'center',
                                    cursor: 'pointer',
                                    background: selectedStaff?.id === member.id ? 'var(--color-surface-hover)' : 'transparent',
                                    borderBottom: '1px solid var(--color-border)',
                                    transition: 'background 0.15s',
                                }}
                            >
                                <div style={{
                                    width: 36, height: 36, borderRadius: '50%',
                                    background: 'var(--color-primary)', color: 'white',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 'var(--text-sm)', fontWeight: 600, flexShrink: 0,
                                }}>
                                    {member.firstName[0]}{member.lastName[0]}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {member.firstName} {member.lastName}
                                    </div>
                                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                                        {roleLabels[member.role] || member.role}
                                        {member.specialty ? ` • ${member.specialty}` : ''}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {filteredStaff.length === 0 && (
                            <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
                                No se encontró personal
                            </div>
                        )}
                    </div>
                </div>

                {/* Chat Area */}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {selectedStaff ? (
                        <>
                            {/* Chat Header */}
                            <div style={{
                                padding: 'var(--space-3) var(--space-4)',
                                borderBottom: '1px solid var(--color-border)',
                                display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                            }}>
                                <div style={{
                                    width: 32, height: 32, borderRadius: '50%',
                                    background: 'var(--color-primary)', color: 'white',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 'var(--text-xs)', fontWeight: 600,
                                }}>
                                    {selectedStaff.firstName[0]}{selectedStaff.lastName[0]}
                                </div>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>{selectedStaff.firstName} {selectedStaff.lastName}</div>
                                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{roleLabels[selectedStaff.role]}</div>
                                </div>
                            </div>

                            {/* Messages */}
                            <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                                {messages.map(msg => (
                                    <div key={msg.id} style={{
                                        display: 'flex', justifyContent: msg.isOwn ? 'flex-end' : 'flex-start',
                                    }}>
                                        <div style={{
                                            maxWidth: '70%', padding: 'var(--space-2) var(--space-3)',
                                            borderRadius: 'var(--radius-lg)',
                                            background: msg.isOwn ? 'var(--color-primary)' : 'var(--color-surface-hover)',
                                            color: msg.isOwn ? 'white' : 'var(--color-text)',
                                            fontSize: 'var(--text-sm)',
                                        }}>
                                            <div>{msg.text}</div>
                                            <div style={{
                                                fontSize: 'var(--text-xs)', marginTop: 4,
                                                opacity: 0.7, textAlign: 'right',
                                            }}>
                                                {msg.timestamp.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Input */}
                            <div style={{
                                padding: 'var(--space-3) var(--space-4)',
                                borderTop: '1px solid var(--color-border)',
                                display: 'flex', gap: 'var(--space-2)',
                            }}>
                                <input
                                    className="form-input"
                                    style={{ flex: 1 }}
                                    placeholder="Escribe un mensaje..."
                                    value={newMessage}
                                    onChange={e => setNewMessage(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && sendMessage()}
                                />
                                <button
                                    className="btn btn-primary"
                                    onClick={sendMessage}
                                    disabled={!newMessage.trim()}
                                >
                                    <Send size={16} />
                                </button>
                            </div>
                        </>
                    ) : (
                        <div style={{
                            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'var(--color-text-muted)',
                        }}>
                            <div style={{ textAlign: 'center' }}>
                                <MessageSquare size={48} style={{ opacity: 0.2, marginBottom: 'var(--space-4)' }} />
                                <p style={{ fontSize: 'var(--text-sm)' }}>Selecciona un miembro del equipo para iniciar una conversación</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
